// src/components/album/StoryCarousel.jsx - Auto-looping story carousel
import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Play, MoreVertical, Eye, Edit, Trash2 } from 'lucide-react';

export const StoryCarousel = ({ 
  stories, 
  mediaItems, 
  onStoryPlay,
  onStoryEdit,
  onStoryDelete,
  isOwner = false,
  primaryColor = '#8b5cf6' 
}) => {
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(true);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  
  const previewIntervalRef = useRef(null);
  const previewTimeoutRef = useRef(null);
  const storyTimeoutRef = useRef(null);

  const safeMediaItems = mediaItems || [];
  const hasStories = stories && stories.length > 0;
  const currentStory = hasStories ? stories[currentStoryIndex] : null;

  // Get media items for current story
  const storyMediaItems = currentStory?.mediaSequence
    ? currentStory.mediaSequence
        .map(mediaId => safeMediaItems.find(m => m.id === mediaId))
        .filter(Boolean)
    : [];

  const currentMedia = storyMediaItems[currentSlideIndex] || storyMediaItems[0];
  const slideDuration = 5000; // 5 seconds per slide

  // Auto-play slideshow effect for slides within current story
  useEffect(() => {
    if (isPreviewPlaying && storyMediaItems.length > 1) {
      previewTimeoutRef.current = setTimeout(() => {
        previewIntervalRef.current = setInterval(() => {
          setCurrentSlideIndex(prevIndex => {
            const nextIndex = prevIndex + 1;
            // When reaching end of slides, advance to next story
            if (nextIndex >= storyMediaItems.length) {
              // Move to next story in the carousel
              setCurrentStoryIndex(prevStoryIndex => {
                const nextStoryIndex = (prevStoryIndex + 1) % stories.length;
                return nextStoryIndex;
              });
              return 0; // Reset to first slide of next story
            }
            return nextIndex;
          });
        }, 5000); // 5 seconds per slide
      }, 500);
    } else {
      if (previewIntervalRef.current) {
        clearInterval(previewIntervalRef.current);
        previewIntervalRef.current = null;
      }
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = null;
      }
      if (!isPreviewPlaying) {
        setCurrentSlideIndex(0);
      }
    }

    return () => {
      if (previewIntervalRef.current) clearInterval(previewIntervalRef.current);
      if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    };
  }, [isPreviewPlaying, storyMediaItems.length, stories.length]);

  // Auto-advance to next story when current story completes (for single-slide stories)
  useEffect(() => {
    if (isPreviewPlaying && storyMediaItems.length === 1 && stories.length > 1) {
      // For stories with only 1 slide, auto-advance after 5 seconds
      const singleSlideTimeout = setTimeout(() => {
        setCurrentStoryIndex(prevIndex => (prevIndex + 1) % stories.length);
      }, 5000);
      
      return () => clearTimeout(singleSlideTimeout);
    }
  }, [isPreviewPlaying, storyMediaItems.length, currentStoryIndex, stories.length]);

  // Reset slide index when story changes
  useEffect(() => {
    setCurrentSlideIndex(0);
    setIsPreviewPlaying(true);
  }, [currentStoryIndex]);

  // Pause on hover/touch, resume when not hovering
  const handleMouseEnter = () => {
    setIsPreviewPlaying(false);
  };

  const handleMouseLeave = () => {
    setIsPreviewPlaying(true);
  };

  // Navigation functions
  const goToNextStory = () => {
    setCurrentStoryIndex(prev => (prev + 1) % stories.length);
  };

  const goToPrevStory = () => {
    setCurrentStoryIndex(prev => (prev - 1 + stories.length) % stories.length);
  };

  const handleEdit = (e, story) => {
    e.stopPropagation();
    setShowActionsMenu(null);
    setIsPreviewPlaying(false);
    if (onStoryEdit) onStoryEdit(story);
  };

  const handleDelete = async (e, story) => {
    e.stopPropagation();
    setShowActionsMenu(null);
    setIsPreviewPlaying(false);
    
    if (window.confirm(`Delete story "${story.title || 'Untitled'}"? This cannot be undone.`)) {
      if (onStoryDelete) await onStoryDelete(story.id);
    }
  };

  const handleView = (e, story) => {
    e.stopPropagation();
    setShowActionsMenu(null);
    if (onStoryPlay) onStoryPlay(story);
  };


  // Early returns AFTER all hooks
  if (!hasStories) {
    return null;
  }

  if (!currentStory) {
    return null;
  }

  const hasMultipleStories = stories.length > 1;

  return (
    <div className="mb-4 bg-transparent rounded-xl p-0">
      {/* Story Preview with Navigation Header */}
      {hasMultipleStories && (
        <div className="flex items-center justify-end mb-2">
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrevStory}
              className="p-2 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-white"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-gray-500">
              {currentStoryIndex + 1} / {stories.length}
            </span>
            <button
              onClick={goToNextStory}
              className="p-2 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-white"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

        {/* Story Preview */}
        <div 
          className="relative bg-white/10 rounded-lg overflow-hidden shadow-sm cursor-pointer group"
          onClick={() => onStoryPlay(currentStory)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleMouseEnter}
          onTouchEnd={handleMouseLeave}
        >
          <div className="max-h-screen mx-auto rounded-t-3xl overflow-hidden">
            {currentMedia ? (
              <img
                src={currentMedia.url}
                alt={currentMedia.title || `Slide ${currentSlideIndex + 1}`}
                className={`w-full h-full object-cover transition-transform duration-500 ${
                  isPreviewPlaying ? 'scale-105' : 'group-hover:scale-105'
                }`}
              />
            ) : (
              <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                <Play className="h-12 w-12 text-gray-400" />
              </div>
            )}
          </div>

          {/* Actions Menu - Owner Only */}
            {isOwner && (
              <div className="absolute top-4 right-4 z-20">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowActionsMenu(showActionsMenu === currentStoryIndex ? null : currentStoryIndex);
                  }}
                  className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors backdrop-blur-sm"
                  aria-label="Story actions"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>

                {/* Dropdown Menu */}
                {showActionsMenu === currentStoryIndex && (
                  <div className="absolute right-0 top-12 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[160px] z-30">
                    <button
                      onClick={(e) => handleView(e, currentStory)}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Eye className="h-4 w-4" />
                      View Story
                    </button>
                    <button
                      onClick={(e) => handleEdit(e, currentStory)}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Edit className="h-4 w-4" />
                      Edit Story
                    </button>
                    <button
                      onClick={(e) => handleDelete(e, currentStory)}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete Story
                    </button>
                  </div>
                )}
              </div>
            )}
          
          {/* Progress Bars for Slides */}
          {storyMediaItems.length > 1 && (
            <div className="absolute bottom-4 left-2 right-2 z-10">
              <div className="flex gap-1">
                {storyMediaItems.map((_, index) => (
                  <div
                    key={index}
                    className={`h-0.5 flex-1 rounded-full transition-all duration-300 ${
                      index === currentSlideIndex && isPreviewPlaying
                        ? 'bg-white/20'
                        : index < currentSlideIndex
                        ? 'bg-white/10'
                        : 'bg-white/30'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Pause Indicator */}
          {!isPreviewPlaying && (
            <div className="absolute top-16 left-4 bg-black/10 text-white px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm">
              Paused
            </div>
          )}

          {/* Playing Indicator */}
          {isPreviewPlaying && (
            <div className="absolute bottom-20 left-4 bg-purple-600 text-white px-3 py-1 rounded-full text-xs flex items-center gap-2 backdrop-blur-sm">
              <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
              Playing
            </div>
          )}

          {/* Play Icon Overlay (shows on hover when paused) */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all flex items-center justify-center">
            <div className={`bg-white/80 rounded-full p-4 transform transition-all duration-200 ${
              isPreviewPlaying ? 'scale-0 opacity-0' : 'scale-0 group-hover:scale-100 group-hover:opacity-100'
            }`}>
              <Play className="h-8 w-8 text-gray-800" />
            </div>
          </div>

          {/* Story Info Overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6">
            <h4 className="text-white font-bold text-lg mb-1">
              {currentStory.title || 'Untitled Story'}
            </h4>
            <p className="text-gray-200 text-sm">
              {storyMediaItems.length} {storyMediaItems.length === 1 ? 'photo' : 'photos'}
            </p>
          </div>
        </div>
    </div>
  );
};